import Modal from '@/components/layout/Modal'
import {
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import React, { FC } from 'react'

import { Button } from '@droidmap/frontend-ui'

import ConfirmCodeInput from './components/ConfirmCodeInput'
import ConfirmFlagsInput from './components/ConfirmFlags'

interface ConfirmationDialogFlag {
  id: string
  label: string
  defaultChecked?: boolean
}

interface ConfirmationDialogProps {
  // Required
  title: string
  description: React.ReactNode
  isOpen: boolean
  onClose: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConfirm: (result: Record<string, boolean> | any) => void

  // Optional
  confirmText?: string
  confirmOptions?: {
    code?: string
    flags?: ConfirmationDialogFlag[]
    /**
     * Optional transformer function to convert boolean flags to a different type
     * Useful for converting to enums like TaskDeletionScope
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transformFlags?: (flags: Record<string, boolean>) => any
  }
  cancelText?: string
  variant?: 'destructive' | 'default'
  isLoading?: boolean
  /** Max width class (default: sm:max-w-md) */
  maxWidth?: string
  /**
   * Optional error/notice content rendered in the dialog body (below the
   * description, above the actions). Use this — not `description` — for block
   * content like an error banner: `description` renders inside a `<p>`, so a
   * `<div>` there triggers an invalid-DOM-nesting warning.
   */
  error?: React.ReactNode
}

const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  title,
  description,
  isOpen,
  onClose,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  isLoading = false,
  confirmOptions = { flags: [] },
  maxWidth = 'sm:max-w-lg',
  error,
}) => {
  const [codeInput, setCodeInput] = React.useState('')
  // Seeded to match the reset rule below, so the very first render already
  // reflects whether a code is required instead of briefly reporting the dialog
  // as confirmable.
  const [codeValid, setCodeValid] = React.useState(
    () => !(typeof confirmOptions.code === 'string' && confirmOptions.code),
  )
  const [checkedOptions, setCheckedOptions] = React.useState<
    Record<string, boolean>
  >({})

  const confirmCode = confirmOptions.code
  const confirmFlags = confirmOptions.flags || []

  const isCodeRequired =
    typeof confirmCode === 'string' && confirmCode.length > 0

  const hasOptions = confirmFlags.length > 0 || isCodeRequired
  const isDestructive = variant === 'destructive'

  // Reset during render when the dialog opens (or the code requirement changes)
  // rather than in an effect. The effect version committed one render of the
  // freshly opened dialog still holding the previous invocation's typed code —
  // and, worse, still reporting it as valid — before clearing it.
  const [resetFor, setResetFor] = React.useState({ isOpen, isCodeRequired })
  if (
    resetFor.isOpen !== isOpen ||
    resetFor.isCodeRequired !== isCodeRequired
  ) {
    setResetFor({ isOpen, isCodeRequired })
    if (isOpen) setCodeInput('')
    setCodeValid(!isCodeRequired)
  }

  return (
    <Modal
      title={title}
      description={description}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      hideCloseButton
      icon={
        isDestructive ? <ExclamationTriangleIcon /> : <QuestionMarkCircleIcon />
      }
      iconWrapperClassName={isDestructive ? 'bg-danger/10' : undefined}
      iconClassName={isDestructive ? 'text-danger' : undefined}
    >
      {error && (
        <div className="border-danger/30 bg-danger-soft rounded-md border px-3 py-2.5">
          {error}
        </div>
      )}
      {hasOptions && (
        <div className="space-y-3 rounded-md border border-border bg-bg-2 p-4">
          {confirmFlags.length > 0 && (
            <ConfirmFlagsInput
              flags={confirmFlags}
              onChange={setCheckedOptions}
            />
          )}
          {isCodeRequired && (
            <ConfirmCodeInput
              confirmCode={confirmCode as string}
              value={codeInput}
              onChange={setCodeInput}
              onValidChange={setCodeValid}
            />
          )}
        </div>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={onClose} variant="outline">
          {cancelText}
        </Button>
        <Button
          onClick={() => {
            const result = confirmOptions.transformFlags
              ? confirmOptions.transformFlags(checkedOptions)
              : checkedOptions
            onConfirm(result)
          }}
          variant={isDestructive ? 'destructive' : 'default'}
          isLoading={isLoading}
          disabled={!codeValid}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  )
}

export default ConfirmationDialog
